import { Card } from "@/components/ui/card";
import { RepAvatar } from "@/components/v2/rep-avatar";
import { cn } from "@/lib/utils";

type Tint = "accent" | "success" | "warning" | "danger" | "neutral";

interface BoardCardProps {
  primaryLabel: string;
  amount?: string;
  secondaryLabel?: string;
  // ADV-1354: the assignee renders through the shared RepAvatar (face-cropped
  // headshot → monogram). `avatarUrl` is the rep's headshot `public_url` when
  // the feeding view carries it; RepAvatar derives initials from `name`.
  assignee?: { name: string; avatarUrl?: string | null };
  recency?: string;
  status?: { label: string; color: Tint };
  // ADV-1361: density is the board's sanctioned per-view control (Display
  // popover Compact switch). "comfortable" is the default operator padding;
  // "compact" tightens it for scanning denser pipelines.
  density?: "comfortable" | "compact";
  onClick?: () => void;
}

const tintClass: Record<Tint, string> = {
  accent: "border-[color:var(--color-accent)] text-[color:var(--color-accent)]",
  success:
    "border-[color:var(--color-success)] text-[color:var(--color-success)]",
  warning:
    "border-[color:var(--color-warning)] text-[color:var(--color-warning)]",
  danger: "border-[color:var(--color-danger)] text-[color:var(--color-danger)]",
  neutral:
    "border-[color:var(--color-border)] text-[color:var(--color-text-md)]",
};

export function BoardCard({
  primaryLabel,
  amount,
  secondaryLabel,
  assignee,
  recency,
  status,
  density = "comfortable",
  onClick,
}: BoardCardProps) {
  const interactive = !!onClick;
  const compact = density === "compact";

  return (
    <Card
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={cn(
        "transition-colors",
        compact ? "p-2" : "p-3",
        interactive && "cursor-pointer hover:bg-accent/5",
      )}
    >
      {/* Top row: primary label + amount pinned top-right. The amount is the
          card's most-scanned value, so it sits in the data family (typo-mono,
          tabular) and never gets pushed by a long, wrapping name. */}
      <div className="flex items-start justify-between gap-2">
        <span className="typo-body font-medium text-[color:var(--color-text-hi)] leading-tight line-clamp-2">
          {primaryLabel}
        </span>
        {amount && (
          <span className="shrink-0 typo-mono tabular-nums font-medium text-[color:var(--color-text-hi)]">
            {amount}
          </span>
        )}
      </div>

      {/* Secondary label */}
      {secondaryLabel && (
        <p className="typo-label mt-0.5 text-[color:var(--color-text-lo)] normal-case tracking-normal line-clamp-1">
          {secondaryLabel}
        </p>
      )}

      {/* Bottom row: status, recency, assignee */}
      <div
        className={cn(
          "flex items-center justify-between gap-2",
          compact ? "mt-1.5" : "mt-2",
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          {status && (
            <span
              className={cn(
                "inline-flex items-center rounded-sm border bg-transparent px-1.5 py-0.5 typo-label shrink-0",
                tintClass[status.color],
              )}
            >
              {status.label}
            </span>
          )}
          {recency && (
            <span className="typo-label text-[color:var(--color-text-lo)] normal-case tracking-normal truncate">
              {recency}
            </span>
          )}
        </div>

        {assignee && (
          <RepAvatar
            name={assignee.name}
            headshotUrl={assignee.avatarUrl}
            size="sm"
          />
        )}
      </div>
    </Card>
  );
}
